import { useAppDispatch } from "@/reducer";
import { useEffect, useMemo } from "react";

import { getEnergyForecastAvailabilityV2 } from "@/api/hooks/micro-service/EnergyForecast";
import { useEnergyForecastApi } from "@/auth";
import { TreeNode } from "@/data-structures";
import { generateEnergyForecastTypeLabel, generateSiteLabel } from "@/energyPlotView/utils";
import { useEnergyForecastAvailability, useEnergyForecastIsLoading } from "@/reducer/selectors/EnergyForecastSelector";
import type { DropdownItem } from "@mm/zeus";

// Extend tree with additional value, findChildren function for finding
// children by value and firstChildren to get default value in dropdown
export class AvailableValueNode extends TreeNode {
  public value?: string;
  public parent: AvailableValueNode | null;
  public children: AvailableValueNode[] = [];

  constructor(parent: AvailableValueNode["parent"], value?: string) {
    super(parent);
    this.parent = parent;
    this.value = value;
  }

  findChildrenNode(value: string) {
    return this.children.find((node) => node.value?.toLowerCase() === value);
  }

  firstChildrenNode(): AvailableValueNode | undefined {
    return this.children?.length ? this.children[0] : undefined;
  }
}

export function useAvailableEnergyForecastDataTree() {
  const dispatch = useAppDispatch();

  const energyForecastApi = useEnergyForecastApi();
  const availability = useEnergyForecastAvailability();
  const isLoading = useEnergyForecastIsLoading();

  useEffect(() => {
    if (energyForecastApi && !availability && !isLoading) {
      dispatch(
        getEnergyForecastAvailabilityV2({
          energyForecastApi,
        }),
      );
    }
  }, [availability, dispatch, energyForecastApi, isLoading]);

  const tree = useMemo(() => {
    // Not very elegant way to construct n-ary tree
    // TODO Consider using recursive function in future for more flexibility if type changes

    if (availability) {
      const root = new AvailableValueNode(null);

      for (const forecastType of Object.keys(availability)) {
        const forecastTypeNode = new AvailableValueNode(root, forecastType);

        for (const model of Object.keys(availability[forecastType])) {
          const modelNode = new AvailableValueNode(forecastTypeNode, model);

          for (const site of availability[forecastType][model].sites as any[]) {
            const siteNode = new AvailableValueNode(modelNode, site.site_id);

            if (site.site_ids?.length > 1) {
              for (const subSiteId of site.site_ids) {
                new AvailableValueNode(siteNode, subSiteId);
              }
            }
          }
        }
      }

      return root;
    }
  }, [availability]);
  return { tree };
}

export function useAvailableEnsMembers(forecastType: string, model: string) {
  const dispatch = useAppDispatch();

  const energyForecastApi = useEnergyForecastApi();
  const availability = useEnergyForecastAvailability();
  const isLoading = useEnergyForecastIsLoading();

  useEffect(() => {
    if (energyForecastApi && !availability && !isLoading) {
      dispatch(
        getEnergyForecastAvailabilityV2({
          energyForecastApi,
        }),
      );
    }
  }, [availability, dispatch, energyForecastApi, isLoading]);
  const modelPrefix = `${model}-`;

  const members: string[] = availability?.[forecastType]?.[model]?.members || [];
  const ensMembers = [];
  const ensQuantiles = [];

  for (const member of members) {
    const cleanMember = member.replace(modelPrefix, "");
    ensMembers.push(cleanMember);
    if (cleanMember.toLowerCase().includes("q-")) {
      ensQuantiles.push(cleanMember);
    }
  }
  return { ensMembers, ensQuantiles };
}

export const ENERGY_ENS_MODELS = ["ecmwf-ens"];

export function transformNodeToDropdownItem(node: AvailableValueNode): DropdownItem {
  return {
    id: node.value || "",
    title: node.value ? generateSiteLabel(node.value) : "",
  };
}

export function transformNodeToDropdownItemForForecastType(node: AvailableValueNode): DropdownItem {
  return {
    id: node.value || "",
    title: node.value ? generateEnergyForecastTypeLabel(node.value) : "",
  };
}
